import { useState } from 'react'
import { ActionIcon, Group, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { faPaperPlaneTop, faCopy, faCheck, faPlus, faMinus } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AdhocContactForm } from './AdhocContactForm'
import { useAddAdhocContact } from '../../hooks/useAddAdhocContact'
import type { Contact } from '../../types/contact'

// Everything a card needs to persist an unmatched contact to the claim (MSD-256),
// provided by ContactsTab, which owns the claim and the duplicate check. The save
// itself is run by this card's own hook so its in-flight state stays local.
export interface AddToClaimHandlers {
  // The connected claim the ad-hoc contact is saved to.
  claimNumber: string
  // The name or the email repeats an existing contact, but not both — the form warns
  // ("Save anyway?") and never blocks (MSD-257). An exact name+email repeat is not
  // reported here: the backend refuses that pair itself (proc Msg 105).
  isPartialDuplicate: (name: string, email: string) => boolean
  // Called after a successful save so the tab can confirm and refetch its contacts.
  onSaved: () => void
}

export interface ContactCardProps {
  contact: Contact
  // Adding recipients only works while composing — false when viewing an existing email.
  canAddToEmail: boolean
  // True when this contact's email is already on the To line — the send button is then
  // disabled, and re-enables when the recipient is removed (driven by ContactsTab).
  alreadyAdded?: boolean
  // Called after this contact was added to the recipients, so the tab can re-read
  // them right away (RecipientsChanged is not delivered in new Outlook web).
  onAdded?: () => void
  // When provided, the + icon on unmatched contacts opens the ad-hoc contact entry
  // form; without it the icon stays disabled (hosts that can't save yet).
  addToClaim?: AddToClaimHandlers
}

// Copy with a fallback for the Outlook add-in iframe, where the Clipboard API is
// blocked by permissions policy. Falls back to legacy execCommand selection copy.
async function copyText(text: string): Promise<boolean> {
  try {
    if (globalThis.navigator?.clipboard?.writeText) {
      await globalThis.navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Clipboard API blocked — fall through to legacy copy.
  }
  try {
    const textarea = globalThis.document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    globalThis.document.body.appendChild(textarea)
    textarea.select()
    const succeeded = globalThis.document.execCommand('copy')
    textarea.remove()
    return succeeded
  } catch (err) {
    console.error('[ContactCard] Copy failed:', err)
    return false
  }
}

function addRecipientToEmail(name: string, email: string, onAdded?: () => void) {
  try {
    const to = (Office?.context?.mailbox?.item as Office.MessageCompose | undefined)?.to
    if (!to?.getAsync || !to?.addAsync) return
    // addAsync appends without deduping, so read the current recipients first and
    // skip if this address is already on the To line (avoids duplicate entries when
    // the button is clicked more than once, or the person is already a recipient).
    to.getAsync((getResult) => {
      if (getResult.status !== Office.AsyncResultStatus.Succeeded) {
        console.error('[ContactCard] Failed to read recipients:', getResult.error)
        return
      }
      const target = email.toLowerCase()
      if (getResult.value.some((r) => r.emailAddress?.toLowerCase() === target)) {
        // Already a recipient (e.g. typed manually) — still notify so the tab syncs.
        onAdded?.()
        return
      }
      to.addAsync([{ displayName: name, emailAddress: email }], (addResult) => {
        if (addResult.status === Office.AsyncResultStatus.Failed) {
          console.error('[ContactCard] Failed to add recipient:', addResult.error)
          return
        }
        onAdded?.()
      })
    })
  } catch (err) {
    console.error('[ContactCard] Failed to add recipient:', err)
  }
}

// No nested ternaries (Sonar S3358).
function getSendTooltip(hasEmail: boolean, canAddToEmail: boolean, alreadyAdded: boolean): string {
  if (!hasEmail) return 'No email available'
  if (!canAddToEmail) return 'Available when composing an email'
  if (alreadyAdded) return 'Already added to email'
  return 'Add to email recipients'
}

// The + icon only expands the ad-hoc entry form; once expanded it becomes a - icon
// that collapses it (MSD-255). No nested ternaries (Sonar S3358).
function getAddToClaimTooltip(canAddToClaim: boolean, formOpen: boolean): string {
  if (!canAddToClaim) return 'Add to claim (coming soon)'
  if (formOpen) return 'Collapse contact form'
  return 'Add to claim'
}

// Ad-hoc contacts show their role when it carries meaning, otherwise "Ad hoc".
// Regular participants always show their role.
function getContactLabel(contact: Contact): string {
  if (!contact.isAdHoc) return contact.role ?? ''
  const role = contact.role ?? ''
  return role && role !== 'Unknown' ? role : 'Ad hoc'
}

export function ContactCard({
  contact,
  canAddToEmail,
  alreadyAdded = false,
  onAdded,
  addToClaim,
}: Readonly<ContactCardProps>) {
  const [copied, setCopied] = useState(false)
  // Inline ad-hoc entry form, expanded under the card row (MSD-256).
  const [formOpen, setFormOpen] = useState(false)
  // One save hook per card: several ad-hoc forms can be expanded at once, and a save
  // on one of them must not put the others' Save buttons into the loading state (MSD-255).
  const { saving, error: saveError, save: saveAdhocContact } = useAddAdhocContact(addToClaim?.claimNumber ?? '')
  // Show nothing when there's no display name; fall back to the email only for labels/actions.
  const displayName = contact.name?.trim() ?? ''
  const emailValue = contact.email ?? ''
  const actionTarget = displayName || emailValue
  const hasEmail = emailValue.length > 0
  const canSend = hasEmail && canAddToEmail
  // Disabled when it can't be sent, or when the address is already on the To line.
  const sendDisabled = !canSend || alreadyAdded
  const label = getContactLabel(contact)
  const handleSend = () => {
    if (!sendDisabled) addRecipientToEmail(displayName, emailValue, onAdded)
  }
  const handleCopy = async () => {
    if (await copyText(emailValue)) {
      setCopied(true)
      globalThis.setTimeout(() => setCopied(false), 1000)
    }
  }
  const handleAddToClaimSave = async (formName: string, formEmail: string) => {
    if (!addToClaim) return
    const saved = await saveAdhocContact(formName, formEmail)
    if (saved) {
      setFormOpen(false)
      addToClaim.onSaved()
    }
  }

  return (
    <Paper withBorder p="xs" shadow="xs">
      <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          {displayName && (
            <Tooltip label={displayName} withArrow>
              <Text fz="xs" fw="var(--mantine-font-weight-semibold)" truncate>
                {displayName}
              </Text>
            </Tooltip>
          )}
          {hasEmail ? (
            <Group gap={6} wrap="nowrap">
              <Tooltip label={copied ? 'Copied' : 'Copy email'} withArrow>
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  color={copied ? 'success.6' : undefined}
                  aria-label={`Copy ${emailValue}`}
                  onClick={handleCopy}
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} size="xs" />
                </ActionIcon>
              </Tooltip>
              <Text fz="xs" c="dimmed" truncate>
                {emailValue}
              </Text>
            </Group>
          ) : (
            <Text fz="xs" c="dimmed" fs="italic" truncate>
              No Email Found in MC1
            </Text>
          )}
          <Group gap="xs" wrap="nowrap">
            <Text fz={10} c="dimmed">
              {label}
            </Text>
            {contact.phone && (
              <Text fz="xs" td="underline" c="var(--link-color)">
                {contact.phone}
              </Text>
            )}
          </Group>
        </Stack>
        <Group gap={4} wrap="nowrap">
          {contact.isUnmatched && (
            <Tooltip label={getAddToClaimTooltip(!!addToClaim, formOpen)} withArrow>
              {/* data-disabled instead of disabled so the explanatory tooltip still fires. */}
              <ActionIcon
                variant="subtle"
                size="lg"
                data-disabled={!addToClaim || undefined}
                aria-disabled={!addToClaim}
                aria-expanded={formOpen}
                aria-label={formOpen ? `Collapse contact form for ${actionTarget}` : `Add ${actionTarget} to claim`}
                onClick={() => {
                  if (addToClaim) setFormOpen((open) => !open)
                }}
              >
                <FontAwesomeIcon icon={formOpen ? faMinus : faPlus} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label={getSendTooltip(hasEmail, canAddToEmail, alreadyAdded)} withArrow>
            <ActionIcon
              variant="subtle"
              size="lg"
              data-disabled={sendDisabled || undefined}
              aria-disabled={sendDisabled}
              aria-label={`Send to ${actionTarget}`}
              onClick={handleSend}
            >
              <FontAwesomeIcon icon={faPaperPlaneTop} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {/* Mounted whenever the card can save, expanded or not: the form renders nothing while
          collapsed, which keeps its edits alive for the next expand (MSD-255). */}
      {addToClaim && (
        <AdhocContactForm
          open={formOpen}
          initialName={displayName}
          initialEmail={emailValue}
          saving={saving}
          apiError={saveError}
          isPartialDuplicate={addToClaim.isPartialDuplicate}
          onSave={handleAddToClaimSave}
          onCancel={() => setFormOpen(false)}
        />
      )}
    </Paper>
  )
}
